import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PlusIcon, PulseIcon } from '@phosphor-icons/react';
import {
  MONITOR_PROTOCOLS,
  MONITOR_PROTOCOL_LABEL,
  SEVERITIES,
  monitorConfig,
  type MonitorCheck,
  type MonitorCheckResult,
  type MonitorsOverview,
  type MonitorConfig,
  type MonitorProtocol,
  type MonitorStatus,
  type Severity,
  type Source,
} from '@meshalert/contract';
import { ApiError, api } from '../../lib/api.ts';
import { SEVERITY_LABEL, relativeTime, uptimePercent, viewerTimeZone } from '../../lib/format.ts';
import { UptimeStrip } from '../../components/MonitorHistoryChart.tsx';
import { useSession } from '../../lib/session.tsx';
import {
  Button,
  Checkbox,
  EmptyState,
  ErrorState,
  Field,
  Input,
  ListSkeleton,
  Panel,
  Select,
  Td,
  Th,
  cx,
} from '../../components/ui.tsx';
import { Dialog } from '../../components/Dialog.tsx';

/**
 * Monitore sind Quellen, die MeshAlert selbst betreibt. Alles, was sie
 * erzeugen, läuft danach durch dieselben Weiterleitungen wie ein Webhook —
 * hier wird nur festgelegt, wann etwas als Ausfall gilt.
 */

export const MONITOR_STATUS_LABEL: Record<MonitorStatus, string> = {
  unknown: 'Noch nicht geprüft',
  up: 'Erreichbar',
  down: 'Ausfall',
};

export const MONITOR_STATUS_CLASS: Record<MonitorStatus, string> = {
  unknown: 'bg-ink-3',
  up: 'bg-accent',
  down: 'bg-critical',
};

/** Voreinstellungen, die für den jeweiligen Dienst tatsächlich üblich sind. */
const DEFAULT_CHECK: Record<MonitorProtocol, MonitorCheck> = {
  http: { protocol: 'http', url: '', method: 'GET', expect_status_min: 200, expect_status_max: 399, allow_insecure_tls: false },
  tcp: { protocol: 'tcp', host: '', port: 443 },
  smtp: { protocol: 'smtp', host: '', port: 25, expect_banner_code: 220 },
  tls_expiry: { protocol: 'tls_expiry', host: '', port: 443, warn_days_before: 14 },
  dns: { protocol: 'dns', hostname: '', record_type: 'A' },
};

export function describeCheck(check: MonitorCheck): string {
  switch (check.protocol) {
    case 'http':
      return check.url;
    case 'tcp':
    case 'smtp':
    case 'tls_expiry':
      return `${check.host}:${check.port}`;
    case 'dns':
      return check.resolver
        ? `${check.record_type} ${check.hostname} @${check.resolver}`
        : `${check.record_type} ${check.hostname}`;
  }
}

export function MonitorsSection() {
  const { can } = useSession();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Source | 'new' | null>(null);

  const monitors = useQuery({ queryKey: ['monitors'], queryFn: () => api.monitors.list() });
  // Derselbe Aufruf wie auf der Übersicht, damit beide aus einem Cache lesen.
  const history = useQuery({
    queryKey: ['stats', 'monitors', '24h', viewerTimeZone],
    queryFn: () => api.stats.monitors({ range: '24h', tz: viewerTimeZone }),
    refetchInterval: 30_000,
  });
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['monitors'] });
    void queryClient.invalidateQueries({ queryKey: ['sources'] });
  };

  const toggle = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      api.monitors.update(id, { is_active: active }),
    onSuccess: invalidate,
  });

  const checkNow = useMutation({
    mutationFn: (id: string) => api.monitors.check(id),
  });

  return (
    <>
      <Panel
        title="Monitore"
        bodyClassName="p-0"
        action={
          can('admin', 'operator') ? (
            <Button size="sm" icon={<PlusIcon size={14} />} onClick={() => setEditing('new')}>
              Monitor anlegen
            </Button>
          ) : null
        }
      >
        {monitors.isLoading ? (
          <ListSkeleton rows={3} />
        ) : monitors.isError ? (
          <ErrorState message="Die Monitore konnten nicht geladen werden." onRetry={() => monitors.refetch()} />
        ) : monitors.data && monitors.data.items.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[58rem] border-collapse">
              <thead className="border-b border-line bg-sunken">
                <tr>
                  <Th>Monitor</Th>
                  <Th>Prüfung</Th>
                  <Th>Zustand</Th>
                  <Th>Letzte 24 Stunden</Th>
                  <Th>Zuletzt</Th>
                  <Th />
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border-subtle)]">
                {monitors.data.items.map((monitor) => (
                  <MonitorRow
                    key={monitor.id}
                    monitor={monitor}
                    history={history.data?.monitors.find((m) => m.id === monitor.id) ?? null}
                    bucket={history.data?.bucket ?? '1h'}
                    canEdit={can('admin', 'operator')}
                    busy={toggle.isPending || checkNow.isPending}
                    lastCheck={checkNow.variables === monitor.id ? checkNow.data ?? null : null}
                    onEdit={() => setEditing(monitor)}
                    onToggle={() => toggle.mutate({ id: monitor.id, active: !monitor.is_active })}
                    onCheck={() => checkNow.mutate(monitor.id)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState
            icon={<PulseIcon size={26} weight="light" />}
            title="Noch kein Monitor"
            description="Ein Monitor prüft von hier aus, ob ein Dienst antwortet — HTTP, ein Port, ein Mailserver, ein Zertifikat, ein DNS-Eintrag. Fällt er aus, entsteht ein Alarm wie jeder andere."
            action={
              can('admin', 'operator') ? (
                <Button variant="primary" size="sm" onClick={() => setEditing('new')}>
                  Ersten Monitor anlegen
                </Button>
              ) : null
            }
          />
        )}
      </Panel>

      {editing ? (
        <MonitorDialog
          monitor={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            invalidate();
          }}
        />
      ) : null}
    </>
  );
}

function MonitorRow({
  monitor,
  history,
  bucket,
  canEdit,
  busy,
  lastCheck,
  onEdit,
  onToggle,
  onCheck,
}: {
  monitor: Source;
  history: MonitorsOverview['monitors'][number] | null;
  bucket: MonitorsOverview['bucket'];
  canEdit: boolean;
  busy: boolean;
  lastCheck: MonitorCheckResult | null;
  onEdit: () => void;
  onToggle: () => void;
  onCheck: () => void;
}) {
  const config = monitor.monitor ?? null;
  const state = monitor.monitor_state ?? null;
  const status: MonitorStatus = monitor.is_active ? state?.status ?? 'unknown' : 'unknown';

  return (
    <tr className={cx('transition-colors hover:bg-hover', !monitor.is_active && 'opacity-55')}>
      <Td>
        <Link
          to={`/quellen/monitore/${monitor.id}`}
          className="block text-[13px] font-medium whitespace-nowrap text-ink underline-offset-2 hover:underline"
        >
          {monitor.name}
        </Link>
        <span className="mt-0.5 block text-[11px] text-ink-3">
          {config ? MONITOR_PROTOCOL_LABEL[config.check.protocol] : 'ohne Konfiguration'}
          {config ? ` · alle ${formatInterval(config.interval_seconds)}` : ''}
          {monitor.is_active ? '' : ' · pausiert'}
        </span>
      </Td>
      <Td>
        <span className="block max-w-[11rem] truncate font-mono text-[11.5px] text-ink-2">
          {config ? describeCheck(config.check) : '—'}
        </span>
      </Td>
      <Td>
        <span className="inline-flex items-center gap-2">
          <span className={cx('size-1.5 rounded-full', MONITOR_STATUS_CLASS[status])} />
          <span className="text-[12.5px] whitespace-nowrap text-ink-2">
            {monitor.is_active ? MONITOR_STATUS_LABEL[status] : 'Pausiert'}
          </span>
          {state?.last_duration_ms !== null && state?.last_duration_ms !== undefined ? (
            <span className="tnum text-[11.5px] text-ink-3">{state.last_duration_ms} ms</span>
          ) : null}
        </span>
        {state?.status === 'down' && state.last_error ? (
          <span className="mt-0.5 block max-w-[12rem] truncate text-[11px] text-critical">
            {state.last_error}
          </span>
        ) : null}
        {state?.cert_expires_at ? (
          <span className="mt-0.5 block text-[11px] text-ink-3">
            Zertifikat bis {new Date(state.cert_expires_at).toLocaleDateString('de-DE')}
          </span>
        ) : null}
        {lastCheck ? (
          <span className={cx('mt-0.5 block text-[11px]', lastCheck.ok ? 'text-accent' : 'text-critical')}>
            Testlauf: {lastCheck.detail}
          </span>
        ) : null}
      </Td>
      <Td>
        {history ? (
          <Link to={`/quellen/monitore/${monitor.id}`} className="block w-24" aria-label={`Verlauf von ${monitor.name}`}>
            <UptimeStrip
              buckets={history.buckets}
              bucket={bucket}
              label={`Erreichbarkeit in 24 Stunden: ${uptimePercent(history.totals.uptime)}`}
            />
            <span className="tnum mt-1 block text-[11px] text-ink-3">{uptimePercent(history.totals.uptime)}</span>
          </Link>
        ) : (
          <span className="text-ink-3">--</span>
        )}
      </Td>
      <Td className="whitespace-nowrap">
        {state?.last_checked_at ? relativeTime(state.last_checked_at) : 'noch nie'}
      </Td>
      <Td className="text-right">
        {canEdit ? (
          <div className="inline-flex gap-1.5">
            <Button size="sm" disabled={busy} onClick={onCheck}>
              Prüfen
            </Button>
            <Button size="sm" disabled={busy} onClick={onToggle}>
              {monitor.is_active ? 'Pausieren' : 'Aktivieren'}
            </Button>
            <Button size="sm" onClick={onEdit}>
              Bearbeiten
            </Button>
          </div>
        ) : null}
      </Td>
    </tr>
  );
}

export function formatInterval(seconds: number): string {
  if (seconds % 3600 === 0) return `${seconds / 3600} h`;
  if (seconds % 60 === 0) return `${seconds / 60} min`;
  return `${seconds} s`;
}

function MonitorDialog({
  monitor,
  onClose,
  onSaved,
}: {
  monitor: Source | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const existing = monitor?.monitor ?? null;
  const [name, setName] = useState(monitor?.name ?? '');
  const [check, setCheck] = useState<MonitorCheck>(existing?.check ?? DEFAULT_CHECK.http);
  const [interval, setInterval] = useState(String(existing?.interval_seconds ?? 60));
  const [timeout, setTimeoutMs] = useState(String(existing?.timeout_ms ?? 10000));
  const [threshold, setThreshold] = useState(String(existing?.failure_threshold ?? 2));
  const [severity, setSeverity] = useState<Severity>(existing?.severity ?? 'critical');

  const draft = (): MonitorConfig | null => {
    const parsed = monitorConfig.safeParse({
      check,
      interval_seconds: Number(interval),
      timeout_ms: Number(timeout),
      failure_threshold: Number(threshold),
      severity,
    });
    return parsed.success ? parsed.data : null;
  };

  const preview = useMutation({
    mutationFn: () => {
      const config = draft();
      if (!config) throw new ApiError('validation_failed', 'Die Angaben sind noch unvollständig', 422);
      return api.monitors.preview(config);
    },
  });

  const save = useMutation({
    mutationFn: () => {
      const config = draft();
      if (!config) throw new ApiError('validation_failed', 'Die Angaben sind noch unvollständig', 422);
      return monitor
        ? api.monitors.update(monitor.id, { name, monitor: config })
        : api.monitors.create({ name, monitor: config });
    },
    onSuccess: onSaved,
  });

  const fieldErrors = save.error instanceof ApiError ? save.error.fieldErrors() : {};
  const generalError =
    save.error instanceof ApiError && Object.keys(fieldErrors).length === 0 ? save.error.message : null;

  return (
    <Dialog title={monitor ? `${monitor.name} bearbeiten` : 'Monitor anlegen'} onClose={onClose}>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <Field label="Name" hint="Steht so im Alarm: „&lt;Name&gt; nicht erreichbar“." error={fieldErrors.name}>
          {(id) => (
            <Input
              id={id}
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Mailserver crat.ch"
            />
          )}
        </Field>

        <Field label="Protokoll">
          {(id) => (
            <Select
              id={id}
              value={check.protocol}
              onChange={(e) => setCheck(DEFAULT_CHECK[e.target.value as MonitorProtocol])}
            >
              {MONITOR_PROTOCOLS.map((p) => (
                <option key={p} value={p}>
                  {MONITOR_PROTOCOL_LABEL[p]}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <CheckFields check={check} onChange={setCheck} />

        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Intervall" hint="Sekunden">
            {(id) => (
              <Select id={id} value={interval} onChange={(e) => setInterval(e.target.value)}>
                {[30, 60, 120, 300, 900, 3600, 21600, 86400].map((s) => (
                  <option key={s} value={s}>
                    {formatInterval(s)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Zeitlimit" hint="Millisekunden">
            {(id) => (
              <Input
                id={id}
                type="number"
                min={500}
                max={60000}
                value={timeout}
                onChange={(e) => setTimeoutMs(e.target.value)}
              />
            )}
          </Field>
          <Field label="Fehlversuche" hint="Bis alarmiert wird">
            {(id) => (
              <Input
                id={id}
                type="number"
                min={1}
                max={10}
                value={threshold}
                onChange={(e) => setThreshold(e.target.value)}
              />
            )}
          </Field>
        </div>

        <Field label="Schweregrad" hint="Bestimmt, wie laut der Alarm zugestellt werden darf.">
          {(id) => (
            <Select id={id} value={severity} onChange={(e) => setSeverity(e.target.value as Severity)}>
              {[...SEVERITIES].reverse().map((s) => (
                <option key={s} value={s}>
                  {SEVERITY_LABEL[s]}
                </option>
              ))}
            </Select>
          )}
        </Field>

        {preview.data ? (
          <div
            className={cx(
              'rounded-md border px-3 py-2.5 text-[12.5px]',
              preview.data.ok
                ? 'border-accent/40 bg-accent-soft text-accent'
                : 'border-critical/40 bg-critical-soft text-critical',
            )}
          >
            {preview.data.ok ? 'Antwortet: ' : 'Fehlgeschlagen: '}
            {preview.data.detail}
            <span className="text-ink-3"> · {preview.data.duration_ms} ms</span>
          </div>
        ) : preview.error ? (
          <p className="text-[12.5px] text-critical">
            {preview.error instanceof ApiError ? preview.error.message : 'Test fehlgeschlagen'}
          </p>
        ) : null}

        {generalError ? (
          <p role="alert" className="text-[13px] text-critical">
            {generalError}
          </p>
        ) : null}

        <div className="flex justify-between gap-2 pt-2">
          <Button type="button" disabled={preview.isPending} onClick={() => preview.mutate()}>
            {preview.isPending ? 'Testet' : 'Jetzt testen'}
          </Button>
          <div className="flex gap-2">
            <Button type="button" onClick={onClose}>
              Abbrechen
            </Button>
            <Button type="submit" variant="primary" disabled={save.isPending}>
              {save.isPending ? 'Wird gespeichert' : monitor ? 'Speichern' : 'Anlegen'}
            </Button>
          </div>
        </div>
      </form>
    </Dialog>
  );
}

/** Die Felder, die genau dieses Protokoll braucht — und keine anderen. */
function CheckFields({
  check,
  onChange,
}: {
  check: MonitorCheck;
  onChange: (next: MonitorCheck) => void;
}) {
  switch (check.protocol) {
    case 'http':
      return (
        <div className="space-y-4">
          <Field label="Adresse">
            {(id) => (
              <Input
                id={id}
                required
                type="url"
                value={check.url}
                onChange={(e) => onChange({ ...check, url: e.target.value })}
                placeholder="https://meshalert.de/health/ready"
              />
            )}
          </Field>
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Methode">
              {(id) => (
                <Select
                  id={id}
                  value={check.method}
                  onChange={(e) => onChange({ ...check, method: e.target.value as 'GET' | 'HEAD' | 'POST' })}
                >
                  {['GET', 'HEAD', 'POST'].map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label="Status ab">
              {(id) => (
                <Input
                  id={id}
                  type="number"
                  min={100}
                  max={599}
                  value={check.expect_status_min}
                  onChange={(e) => onChange({ ...check, expect_status_min: Number(e.target.value) })}
                />
              )}
            </Field>
            <Field label="Status bis">
              {(id) => (
                <Input
                  id={id}
                  type="number"
                  min={100}
                  max={599}
                  value={check.expect_status_max}
                  onChange={(e) => onChange({ ...check, expect_status_max: Number(e.target.value) })}
                />
              )}
            </Field>
          </div>
          <Field label="Antwort muss enthalten" hint="Optional. Fängt die Seite ab, die 200 liefert und trotzdem kaputt ist.">
            {(id) => (
              <Input
                id={id}
                value={check.expect_body_contains ?? ''}
                onChange={(e) => onChange({ ...check, expect_body_contains: e.target.value || null })}
                placeholder='"status":"ok"'
              />
            )}
          </Field>
          <Checkbox
            label="Ungültige TLS-Zertifikate akzeptieren"
            description="Nur für interne Dienste mit selbstsigniertem Zertifikat."
            checked={check.allow_insecure_tls}
            onChange={(e) => onChange({ ...check, allow_insecure_tls: e.target.checked })}
          />
        </div>
      );

    case 'tcp':
      return (
        <div className="grid gap-4 sm:grid-cols-[1fr_8rem]">
          <Field label="Host">
            {(id) => (
              <Input
                id={id}
                required
                value={check.host}
                onChange={(e) => onChange({ ...check, host: e.target.value })}
                placeholder="db.example.com"
              />
            )}
          </Field>
          <Field label="Port">
            {(id) => (
              <Input
                id={id}
                required
                type="number"
                min={1}
                max={65535}
                value={check.port}
                onChange={(e) => onChange({ ...check, port: Number(e.target.value) })}
              />
            )}
          </Field>
        </div>
      );

    case 'smtp':
      return (
        <>
          <div className="grid gap-4 sm:grid-cols-[1fr_8rem]">
            <Field label="Mailserver">
              {(id) => (
                <Input
                  id={id}
                  required
                  value={check.host}
                  onChange={(e) => onChange({ ...check, host: e.target.value })}
                  placeholder="mail.example.com"
                />
              )}
            </Field>
            <Field label="Port" hint="25, 465 oder 587">
              {(id) => (
                <Input
                  id={id}
                  required
                  type="number"
                  min={1}
                  max={65535}
                  value={check.port}
                  onChange={(e) => onChange({ ...check, port: Number(e.target.value) })}
                />
              )}
            </Field>
          </div>
          <p className="text-[12px] text-ink-3">
            Geprüft wird die Begrüssung des Servers, nicht nur der offene Port: ein Mailserver, der
            kein 220 schickt, nimmt auch keine Mail an.
          </p>
        </>
      );

    case 'tls_expiry':
      return (
        <div className="grid gap-4 sm:grid-cols-[1fr_7rem_9rem]">
          <Field label="Host">
            {(id) => (
              <Input
                id={id}
                required
                value={check.host}
                onChange={(e) => onChange({ ...check, host: e.target.value })}
                placeholder="meshalert.de"
              />
            )}
          </Field>
          <Field label="Port">
            {(id) => (
              <Input
                id={id}
                required
                type="number"
                min={1}
                max={65535}
                value={check.port}
                onChange={(e) => onChange({ ...check, port: Number(e.target.value) })}
              />
            )}
          </Field>
          <Field label="Warnen ab" hint="Tage vorher">
            {(id) => (
              <Input
                id={id}
                type="number"
                min={1}
                max={365}
                value={check.warn_days_before}
                onChange={(e) => onChange({ ...check, warn_days_before: Number(e.target.value) })}
              />
            )}
          </Field>
        </div>
      );

    case 'dns':
      return (
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-[1fr_8rem]">
            <Field label="Name">
              {(id) => (
                <Input
                  id={id}
                  required
                  value={check.hostname}
                  onChange={(e) => onChange({ ...check, hostname: e.target.value })}
                  placeholder="meshalert.de"
                />
              )}
            </Field>
            <Field label="Typ">
              {(id) => (
                <Select
                  id={id}
                  value={check.record_type}
                  onChange={(e) =>
                    onChange({ ...check, record_type: e.target.value as typeof check.record_type })
                  }
                >
                  {['A', 'AAAA', 'MX', 'TXT', 'CNAME'].map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Erwarteter Wert" hint="Optional. Einer der Einträge muss das enthalten.">
              {(id) => (
                <Input
                  id={id}
                  value={check.expect_contains ?? ''}
                  onChange={(e) => onChange({ ...check, expect_contains: e.target.value || null })}
                  placeholder="192.0.2.10"
                />
              )}
            </Field>
            <Field label="Nameserver" hint="Optional. Sonst antwortet der Standard-Resolver aus dem Cache.">
              {(id) => (
                <Input
                  id={id}
                  value={check.resolver ?? ''}
                  onChange={(e) => onChange({ ...check, resolver: e.target.value || null })}
                  placeholder="192.0.2.53"
                  className="font-mono"
                />
              )}
            </Field>
          </div>
        </div>
      );
  }
}
